import {createAsyncThunk} from '@reduxjs/toolkit';
import {toast} from 'react-toastify';

import {config} from '@platform/constants';
import {httpService} from '@shared/utilities';
import {AscResponse, NOTETYPES} from '@shared/models';
import * as Response from '@shared/models/journal/journal.response.type';
import * as Request from '@shared/models/journal/journal.request.type';
import {formatErrorMessage} from '@ascendnow/ascend-components';

import {setJournalNotes, setUpdateSingleNote} from '../slices/note.slice';

export const JournalEndpoints = {
  getNotes: config.base_url + '/notes',
  createNote: config.base_url + '/notes',
  updateNote: (noteId: string) => config.base_url + `/notes/${noteId}`,
  deleteNote: (noteId: string) => config.base_url + `/notes/${noteId}`,
  addTagToNote: (tagId: string) => config.base_url + `/notes/${tagId}/tag`,
  deleteTagFromNote: (tagId: string) => config.base_url + `/notes/tags/${tagId}`,
  uploadFile: (challengeId: string, type: 'image' | 'video') =>
    config.base_url + `/challenges/${challengeId}/submit/file/${type}`,
};

export const fetchJournalNotes = createAsyncThunk<void, Request.IGetNotesQueryType>(
  JournalEndpoints.getNotes,
  async (query, {dispatch}) => {
    try {
      const {search, tag, type, contains, sort} = query.params;
      const params = new URLSearchParams({type});
      if (search && search.length > 0) params.append('search', search);
      if (sort && sort.length > 0) params.append('sort', sort);
      if (tag && tag.length > 0) params.append('tag', tag);
      if (contains && contains.length > 0) params.append('contains', contains);
      const res = await httpService.get<AscResponse<Response.INoteListResponseType>>(
        `${JournalEndpoints.getNotes}${params ? '?' + params : ''}`
      );

      if (type === NOTETYPES.MODULE || type === NOTETYPES.TRACK) {
          dispatch(
              setJournalNotes({
                  notes: res.data.data.notes?.map((el) => ({
                      ...el,
                      id: el?.moduleId,
                      title: el?.moduleName,
                      modules: el?.notes,
                      noteTags: el?.moduleTags,
                  })),
                  noteTags: res.data.data.noteTags.map((el) => el['name']),
              })
          );
      } else {
          dispatch(
              setJournalNotes({
                  notes: res.data.data.notes,
                  noteTags: res.data.data.noteTags.map((el) => el['name']),
              })
          );
      }

      if (query.cb && query.cb.onSuccess) {
        query.cb.onSuccess(res.data.data);
      }
    } catch (err) {
      if (query.cb && query.cb.onError) {
        query.cb.onError(err);
      }
      console.log(formatErrorMessage(err, `Error fetching notes, query ${JournalEndpoints.getNotes}`), {
        type: 'error',
      });
    }
  }
);

export const createNote = createAsyncThunk<void, Request.ICreateNoteQueryType>(
  JournalEndpoints.createNote + '/create',
  async (query, {dispatch}) => {
    try {
      const res = await httpService.post<AscResponse<Response.ICreateNoteResponseType>>(
        `${JournalEndpoints.createNote}`,
        query.params
      );

      if (query.cb && query.cb.onSuccess) {
        query.cb.onSuccess(res.data.data);
      }
    } catch (err) {
      if (query.cb && query.cb.onError) {
        query.cb.onError(err);
      }
      console.log(formatErrorMessage(err, `Error creating a note, query ${JournalEndpoints.createNote}`), {
        type: 'error',
      });
    }
  }
);

export const updateNote = createAsyncThunk<void, Request.IUpdateNoteQueryType>(
  JournalEndpoints.updateNote('update/id'),
  async (query, {dispatch}) => {
    if (query.params.noteId) {
      try {
        const res = await httpService.patch<AscResponse<Response.IUpdateNoteResponseType>>(
          `${JournalEndpoints.updateNote(query.params.noteId)}?type=${query.params.type}`,
          query.params
        );

        dispatch(setUpdateSingleNote(res.data.data));
        toast('The note has been successfully updated', {
          type: 'success',
        });

        if (query.cb && query.cb.onSuccess) {
          query.cb.onSuccess(res.data.data);
        }
      } catch (err) {
        if (query.cb && query.cb.onError) {
          query.cb.onError(err);
        }
        console.log(
          formatErrorMessage(
            err,
            `Error updating a note, query: ${JournalEndpoints.updateNote('update/id')}`
          ),
          {
            type: 'error',
          }
        );
      }
    }
  }
);

export const deleteNote = createAsyncThunk<void, Request.IDeleteNoteQueryType>(
  JournalEndpoints.deleteNote('id'),
  async (query, {dispatch}) => {
    try {
      const res = await httpService.delete<AscResponse<{ isDeleted: boolean }>>(
        `${JournalEndpoints.deleteNote(query.params.noteId)}?type=${query.params.type}`
      );

      if (res.data.data && res.data.data.isDeleted) {
        toast('The note has been successfully deleted', {
          type: 'success',
        });
      }

      if (query.cb && query.cb.onSuccess) {
        query.cb.onSuccess(res.data.data);
      }
    } catch (err) {
      if (query.cb && query.cb.onError) {
        query.cb.onError(err);
      }
      console.log(formatErrorMessage(err, `Error deleting a note, query: ${JournalEndpoints.deleteNote('id')}`), {
        type: 'error',
      });
    }
  }
);

export const addTagToNote = createAsyncThunk<void, Request.IAddTagToNoteQueryType>(
  JournalEndpoints.addTagToNote('id'),
  async (query, {dispatch}) => {
    try {
      const res = await httpService.post<AscResponse<Response.INoteResponseType>>(
        `${JournalEndpoints.addTagToNote(query.params.noteId)}?type=${query.params.type}`,
        {
          tag: query.params.tag,
        }
      );

      if (res.data && res.data.data) {
        toast('The tag has been successfully added', {
          type: 'success',
        });
      }

      if (query.cb && query.cb.onSuccess) {
        query.cb.onSuccess(res.data.data);
      }
    } catch (err) {
      if (query.cb && query.cb.onError) {
        query.cb.onError(err);
      }
      console.log(
        formatErrorMessage(err, `Error adding tag to a note, query: ${JournalEndpoints.addTagToNote('id')}`),
        {
          type: 'error',
        }
      );
    }
  }
);

export const removeTagFromNote = createAsyncThunk<void, Request.IRemoveTagFromNoteQueryType>(
  JournalEndpoints.deleteTagFromNote('id'),
  async (query, {dispatch}) => {
    try {
      const res = await httpService.delete<AscResponse<{ isDeleted: boolean }>>(
        `${JournalEndpoints.deleteTagFromNote(query.params.tagId)}`
      );

      if (query.cb && query.cb.onSuccess) {
        query.cb.onSuccess(res.data.data);
      }
    } catch (err) {
      if (query.cb && query.cb.onError) {
        query.cb.onError(err);
      }
      console.log(
        formatErrorMessage(
          err,
          `Error deleting tag from a note, query: ${JournalEndpoints.deleteTagFromNote('id')}`
        ),
        {
          type: 'error',
        }
      );
    }
  }
);

export const uploadFileToChallenge = createAsyncThunk<void, Request.IUploadFileToChallengeQueryType>(
  JournalEndpoints.uploadFile('id', 'image'),
  (query, {dispatch}) => {
    httpService
      .post(
        `${JournalEndpoints.uploadFile(query.params.challengeId, query.params.type)}`,
        query.params.formData,
        {headers: {'Content-Type': 'multipart/form-data'}}
      )
      .then((res) => {
        if (query.cb && query.cb.onSuccess) {
          query.cb.onSuccess(res.data.data);
        }
      })
      .catch((err) => {
        if (query.cb && query.cb.onError) {
          query.cb.onError(err);
        }
        console.log(
          formatErrorMessage(
            err,
            `Error uploading file to a challenge note, query: ${JournalEndpoints.addTagToNote('id')}`
          ),
          {
            type: 'error',
          }
        );
      });
  }
);
